import { useState, useRef, useEffect } from 'react'

const cards = [
  {
    title: 'Just getting started',
    description: "You want to invest confidently but don't know where to begin.",
    image: '/images/elite/broke/carousel-1.png'
  },
  {
    title: 'Prefer disciplined investing',
    description: "You earn well but don't have the time to track markets or rebalance often.",
    image: '/images/elite/broke/carousel-2.png'
  },
  {
    title: 'Long-term mindset',
    description: "You're focused on steady wealth growth, not short-term market noise.",
    image: '/images/elite/broke/carousel-3.png'
  }
]

export const BrokeEliteMembershipCarousel = () => {
  const scrollRef = useRef<HTMLUListElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(false)

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current
    if (!el) return
    const clamped = Math.max(0, Math.min(index, cards.length - 1))
    const child = el.children[clamped] as HTMLElement | undefined
    child?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
    setActiveIndex(clamped)
  }

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = 204 + 16
    const next = Math.round(el.scrollLeft / cardWidth)
    setActiveIndex(Math.max(0, Math.min(next, cards.length - 1)))
  }

  useEffect(() => {
    if (!isAutoPlaying) return
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % cards.length
        scrollToIndex(next)
        return next
      })
    }, 4000)
    return () => window.clearInterval(timer)
  }, [isAutoPlaying])

  return (
    <div className='bg-white'>
      <h2 className='text-base font-medium text-grey10 mb-4 px-5 m-0'>Who is Elite membership for?</h2>

      <ul
        ref={scrollRef}
        onScroll={handleScroll}
        role='list'
        aria-label='Who is Elite membership for'
        className='flex overflow-x-auto gap-4 scrollbar-hide snap-mandatory pb-8 pl-5 list-none m-0 p-0'
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {cards.map((card, index) => (
          <li
            key={index}
            role='listitem'
            aria-label={`${card.title}, ${card.description}, ${index + 1} of ${cards.length}`}
            className='w-[204px] max-w-[204px] h-full max-h-[268px] overflow-clip relative bg-[#FFF8EE] rounded-3xl flex flex-col justify-between snap-start border border-orange-100 flex-shrink-0 list-none'
          >
            <div aria-hidden='true' className='px-5 pt-5'>
              <h3 className='text-[#992E00] text-13 font-medium mb-2 m-0'>{card.title}</h3>
              <p className='text-gray-800 text-sm leading-relaxed m-0'>{card.description}</p>
            </div>
            <div aria-hidden='true' className='mt-8 flex justify-center relative left-[40px] bottom-10'>
              <img
                src={card.image}
                alt=''
                className='w-[180px] h-[180px] object-cover grayscale sepia contrast-110'
              />
            </div>
          </li>
        ))}
        <li className='min-w-[20px] flex-shrink-0 list-none' aria-hidden='true' />
      </ul>

      <div className='flex items-center justify-center gap-3 px-5 pb-4'>
        <button
          type='button'
          aria-label='Previous slide'
          onClick={() => {
            setIsAutoPlaying(false)
            scrollToIndex(activeIndex - 1)
          }}
          disabled={activeIndex === 0}
          className='min-h-[24px] min-w-[24px] px-2 py-1 text-[12px] font-medium text-[#232323] bg-[#EBF2F2] border-none rounded-lg cursor-pointer disabled:opacity-40'
        >
          Previous
        </button>
        <button
          type='button'
          aria-label={isAutoPlaying ? 'Pause slides' : 'Play slides'}
          onClick={() => setIsAutoPlaying((v) => !v)}
          className='min-h-[24px] min-w-[24px] px-2 py-1 text-[12px] font-medium text-[#232323] bg-[#EBF2F2] border-none rounded-lg cursor-pointer'
        >
          {isAutoPlaying ? 'Pause' : 'Play'}
        </button>
        <button
          type='button'
          aria-label='Next slide'
          onClick={() => {
            setIsAutoPlaying(false)
            scrollToIndex(activeIndex + 1)
          }}
          disabled={activeIndex === cards.length - 1}
          className='min-h-[24px] min-w-[24px] px-2 py-1 text-[12px] font-medium text-[#232323] bg-[#EBF2F2] border-none rounded-lg cursor-pointer disabled:opacity-40'
        >
          Next
        </button>
      </div>

      <div className='flex justify-center mt-2 px-5 pb-8' aria-hidden='true'>
        <div className='w-48 h-1 bg-gray-200 rounded-full overflow-hidden relative'>
          <div
            className='absolute top-0 left-0 h-full bg-gray-800 transition-all duration-150 ease-out'
            style={{ width: '33.33%', transform: `translateX(${(activeIndex / Math.max(cards.length - 1, 1)) * 200}%)` }}
          />
        </div>
      </div>
    </div>
  )
}
